import React, { useRef, useState, useEffect } from "react";
import { isMobile } from "react-device-detect";
import { Vector3 } from "three";
import { useNavigate } from "react-router-dom";
import { useThree, useFrame } from "@react-three/fiber";
import { Captain, Commander, Junior, Senior, Trainee } from "../Roles";
import { getFileUrl, getVector } from "../utils/format-utils";
import { debounce } from "../utils/debounce-utils";
import { Earth } from "../Earth";
import { Coin } from "../Coin";
import { OrbitControls, Stars, useGLTF, useProgress } from "@react-three/drei";
import * as THREE from "three";

interface Props {
  onProgressUpdate: (progress: number) => void;
  setIsLoaded: (value: boolean) => void;
}

export const Scene = ({ onProgressUpdate, setIsLoaded }: Props) => {
  const [rockets, setRockets] = useState<any[]>([]);
  const navigate = useNavigate();
  const { progress } = useProgress();
  const controlsRef = useRef<any>(null);
  const { camera } = useThree();
  const initialTarget = new THREE.Vector3(0, 0, 0); // 初始 target
  const initialPosition = new THREE.Vector3(2, 0, 8); // 初始相机位置
  const duration = 5; // 动画持续时间（秒）
  const timerRef = useRef<number | null>(null); // 保存倒计时的引用
  const isResettingRef = useRef(false); // 是否正在重置
  const modelUrls = [
    getFileUrl("captain.glb"),
    getFileUrl("earth.glb"),
    getFileUrl("commander.glb"),
    getFileUrl("trainee.glb"),
    getFileUrl("junior.glb"),
    getFileUrl("senior.glb"),
    getFileUrl("coin.glb"),
  ];

  // 初始化时生成一个火箭
  useEffect(() => {
    modelUrls.forEach((url) => {
      useGLTF.preload(url, "/draco/");
    });
    addRocket();
  }, []);

  useEffect(() => {
    if (progress === 100) {
      setIsLoaded(true); // 当进度达到 100% 时触发
    }
  }, [progress, setIsLoaded]);

  const addRocket = () => {
    const id = Math.random();
    const speed = 0.05; // 火箭移动速度
    const clockwise = Math.random() < 0.5;
    const position = new Vector3(0, 0, 0);

    // 在地球中心生成新的火箭
    setRockets((prevRockets) => [
      ...prevRockets,
      {
        id,
        component: (
          <Captain
            key={id}
            id={id}
            url={getFileUrl("captain.glb")}
            remove={removeRocket}
            speed={speed}
            scale={1.3}
            position={position}
            direction={clockwise}
            getCharacters={getCharacters}
          />
        ),
      },
    ]);
  };

  // 防抖的 addRocket 函数，3 秒内只允许触发一次
  const debouncedAddRocket = debounce(() => {
    addRocket();
  }, 1000); // 1 秒的防抖间隔

  // 移除火箭
  const removeRocket = (id: number) => {
    setRockets((prevRockets) =>
      prevRockets.filter((rocket) => rocket.id !== id),
    );
    setTimeout(() => {
      debouncedAddRocket(); // 移除后重新生成一个火箭
    }, 2000);
  };

  // 防抖的 onclick 函数，1 秒内只允许触发一次
  const getCharacters = debounce((name: string) => {
    switch (name) {
      case "commander":
        navigate("/popular");
        break;
      case "senior":
        navigate("/universe");
        break;
      case "captain":
        navigate("/orbit");
        break;
      case "trainee":
        navigate("/nova");
        break;
      default:
        navigate("/");
    }
    //navigate(`/character/${name}`);
  }, 10); // 1 秒的防抖间隔

  const smoothResetOrbitControls = () => {
    if (!controlsRef.current) return;

    const controls = controlsRef.current;
    const startPosition = camera.position.clone(); // 当前相机位置
    const startTarget = controls.target.clone(); // 当前目标
    const endPosition = initialPosition.clone();
    const endTarget = initialTarget.clone();

    let elapsedTime = 0;
    const totalTime = 5 * 1000; // 10 秒
    const animationFrame = (timestamp: number) => {
      if (!isResettingRef.current) return;

      elapsedTime += 16; // 每帧大约 16ms
      const t = Math.min(elapsedTime / totalTime, 1); // 归一化时间 (0~1)

      // 平滑插值
      camera.position.lerpVectors(startPosition, endPosition, t);
      controls.target.lerpVectors(startTarget, endTarget, t);
      controls.update();

      if (t < 1) {
        requestAnimationFrame(animationFrame);
      } else {
        isResettingRef.current = false;
        controls.enabled = true; // 恢复交互
        onProgressUpdate(0); // 重置进度条
      }
    };

    isResettingRef.current = true;
    controls.enabled = false; // 禁用交互
    requestAnimationFrame(animationFrame);
  };

  const startCountdown = () => {
    if (isResettingRef.current) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    let elapsedTime = 0;

    // 启动倒计时
    timerRef.current = window.setInterval(() => {
      elapsedTime += 1;
      const progress = (elapsedTime / duration) * 100;
      onProgressUpdate(progress); // 更新进度条

      if (elapsedTime >= duration) {
        clearInterval(timerRef.current!);
        smoothResetOrbitControls();
      }
    }, 1000);
  };

  useEffect(() => {
    const controls = controlsRef.current;

    if (controls) {
      const handleStart = () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        onProgressUpdate(1); // 停止时重置进度条
      };

      const handleEnd = () => {
        startCountdown();
      };

      controls.addEventListener("start", handleStart);
      controls.addEventListener("end", handleEnd);

      return () => {
        controls.removeEventListener("start", handleStart);
        controls.removeEventListener("end", handleEnd);
      };
    }
  }, []);

  return (
    <>
      <ambientLight intensity={0.5} />

      <Earth
        url={getFileUrl("earth.glb")}
        scale={isMobile ? 1.3 : 2}
        getCharacters={getCharacters}
      />
      <Commander
        url={getFileUrl("commander.glb")}
        scale={isMobile ? 3 : 5}
        position={getVector(isMobile ? [1, 1.4, 0] : [2, 2, 0])}
        getCharacters={getCharacters}
      />
      <Trainee
        url={getFileUrl("trainee.glb")}
        scale={isMobile ? 0.28 : 0.5}
        position={getVector(isMobile ? [1, -1.2, -1] : [2.5, -2.8, -1])}
        getCharacters={getCharacters}
      />
      <Junior
        url={getFileUrl("junior.glb")}
        scale={isMobile ? 0.2 : 0.4}
        position={getVector(isMobile ? [-1, -1, 0.3] : [-2, -2, 0.3])}
        getCharacters={getCharacters}
      />
      <Senior
        url={getFileUrl("senior.glb")}
        scale={isMobile ? 0.4 : 0.8}
        position={getVector(isMobile ? [-1, 1.2, -1.5] : [-2.2, 1.8, -1.5])}
        getCharacters={getCharacters}
      />
      <Coin
        url={getFileUrl("coin.glb")}
        scale={isMobile ? 0.06 : 0.1}
        position={getVector(isMobile ? [-0.5, 1, 2.5] : [-2.5, 2, 0.5])}
        getCharacters={getCharacters}
      />
      {rockets.map((rocket) => rocket.component)}

      <Stars
        radius={20}
        depth={50}
        count={100}
        factor={7}
        saturation={0}
        fade
      />
      <OrbitControls
        target={[initialTarget.x, initialTarget.y, initialTarget.z]}
        ref={controlsRef}
        enablePan={false}
        enableZoom={false}
        enableRotate={true}
      />
    </>
  );
};
